import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, CheckCircle2, CircleHelp, Lightbulb, Paperclip, Send, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { FEEDBACK_KINDS } from '../lib/feedbackKinds';

const HIDDEN_PATHS = new Set(['/auth/callback']);
const IMAGE_LIMIT = 10 * 1024 * 1024;
const VIDEO_LIMIT = 50 * 1024 * 1024;
const FEEDBACK_MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime';

const ALLOWED_MEDIA_RULES = {
  'image/jpeg': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.jpg', '.jpeg'] },
  'image/png': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.png'] },
  'image/webp': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.webp'] },
  'image/gif': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.gif'] },
  'video/mp4': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.mp4'] },
  'video/webm': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.webm'] },
  'video/quicktime': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.mov', '.qt'] },
};

function getOrCreateFeedbackSessionId() {
  try {
    const existing = sessionStorage.getItem('feedback_session_id');
    if (existing) {
      return existing;
    }
    const created = `feedback_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('feedback_session_id', created);
    return created;
  } catch (error) {
    return `feedback_${Date.now()}`;
  }
}

function validateAttachment(file) {
  if (!file) {
    return null;
  }

  const mime = (file.type || '').toLowerCase();
  const rule = ALLOWED_MEDIA_RULES[mime];
  const dotIndex = file.name.lastIndexOf('.');
  const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';

  if (!rule) {
    return 'Attachment must be JPEG, PNG, WEBP, GIF, MP4, WEBM, or MOV.';
  }

  if (!rule.extensions.includes(extension)) {
    return `File extension does not match ${mime}.`;
  }

  if (rule.kind === 'image' && file.size > IMAGE_LIMIT) {
    return 'Images must be 10MB or smaller.';
  }

  if (rule.kind === 'video' && file.size > VIDEO_LIMIT) {
    return 'Videos must be 50MB or smaller.';
  }

  return null;
}

export default function FeedbackWidget() {
  const location = useLocation();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaError, setMediaError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentWithEmail, setSentWithEmail] = useState(false);
  const messageRef = useRef(null);
  const openerRef = useRef(null);
  const dialogRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const shouldHide = HIDDEN_PATHS.has(location.pathname);
  const copy = FEEDBACK_KINDS[type] || FEEDBACK_KINDS.bug;

  const sessionId = useMemo(() => getOrCreateFeedbackSessionId(), []);

  const openModal = () => {
    openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
    setOpen(true);
    setSubmitError('');
    setSubmitSuccess('');
  };

  useEffect(() => {
    window.addEventListener('lilycove:open-feedback', openModal);
    return () => {
      window.removeEventListener('lilycove:open-feedback', openModal);
    };
  }, []);

  const closeModal = () => {
    setOpen(false);
    setSubmitError('');
    setSubmitSuccess('');
    if (openerRef.current && openerRef.current.focus) openerRef.current.focus();
  };

  // WO-35: focus the message box on open, Escape closes, the page behind does not scroll.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => messageRef.current && messageRef.current.focus(), 50);
    const onKey = (e) => {
      if (e.key === 'Escape') { closeModal(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      // keep Tab inside the dialog
      const items = [...dialogRef.current.querySelectorAll('button, [href], input:not([type=hidden]):not(.hidden), textarea, select')]
        .filter((el) => !el.disabled && el.getAttribute('tabindex') !== '-1' && el.getAttribute('aria-hidden') !== 'true');
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (shouldHide) {
    return null;
  }

  const resetForm = () => {
    setMessage('');
    setEmail('');
    setHoneypot('');
    setMedia(null);
    setMediaError('');
  };

  const sendAnother = () => {
    setSubmitSuccess('');
    setTimeout(() => messageRef.current && messageRef.current.focus(), 50);
  };

  const onAttachmentChange = (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    const validationError = validateAttachment(file);

    if (validationError) {
      setMedia(null);
      setMediaError(validationError);
      return;
    }

    setMedia(file);
    setMediaError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!message.trim()) {
      setSubmitError(copy.empty);
      if (messageRef.current) messageRef.current.focus();
      return;
    }

    if (mediaError) {
      setSubmitError(mediaError);
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const context = {
        route_path: location.pathname,
        path: location.pathname,
        query: window.location.search,
        url: window.location.href,
        referrer: document.referrer || null,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        dpr: window.devicePixelRatio || 1,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        user_agent: navigator.userAgent,
        platform: 'web',
        session_id: sessionId,
        user_id: user?.id || null,
        timestamp: new Date().toISOString(),
      };

      const formData = new FormData();
      formData.append('type', type);
      formData.append('message', message.trim());
      formData.append('honeypot', honeypot);
      formData.append('context_json', JSON.stringify(context));

      if (email.trim()) {
        formData.append('email', email.trim());
      }

      if (media) {
        formData.append('media', media);
      }

      const headers = {
        'X-Session-ID': sessionId,
      };

      if (user?.id) {
        headers['X-User-ID'] = user.id;
      }

      const response = await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        const detail = payload?.detail || payload?.error || 'Could not send this right now. Please try again.';
        throw new Error(detail);
      }

      setSentWithEmail(!!email.trim());
      resetForm();
      setSubmitSuccess(copy.thanks);
    } catch (error) {
      setSubmitError(error.message || 'Could not send this right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full rounded-lg border border-purple/20 bg-dark/80 px-3 py-2 text-sm text-cream placeholder:text-cream/30 focus:border-purple/50 focus:outline-none focus:ring-2 focus:ring-purple/40';

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={openModal}
          className="fixed right-4 z-40 h-12 w-12 rounded-full bg-purple text-white shadow-xl shadow-purple/40 transition hover:bg-purple/90"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          aria-label="Give feedback"
          title="Report a problem or suggest a feature"
        >
          <CircleHelp className="mx-auto h-5 w-5" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={closeModal}
        >
          <div
            role="dialog"
            ref={dialogRef}
            aria-modal="true"
            aria-labelledby="feedback-title"
            data-testid="feedback-dialog"
            className="w-full max-h-[92vh] overflow-y-auto rounded-t-2xl border border-purple/30 bg-[#12121b] px-5 pt-3 sm:max-w-md sm:rounded-xl sm:pt-5"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1.25rem)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-cream/20 sm:hidden" aria-hidden="true" />
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 id="feedback-title" className="text-lg font-semibold text-cream">{copy.title}</h2>
                <p className="text-sm text-cream/60">{copy.subtitle}</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="-mr-1 rounded-lg p-1 text-cream/60 transition hover:bg-white/10 hover:text-cream"
                aria-label="Close feedback form"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {submitSuccess ? (
              <div className="py-6 text-center" role="status" data-testid="feedback-success">
                <CheckCircle2 className="mx-auto h-10 w-10 text-green" />
                <p className="mt-3 text-cream">{submitSuccess}</p>
                <p className="mt-1 text-sm text-cream/50">{sentWithEmail ? 'We will reply by email if we need more details.' : 'Add your email next time if you would like a reply.'}</p>
                <div className="mt-5 flex justify-center gap-2">
                  <button type="button" onClick={sendAnother}
                          className="rounded-lg px-4 py-2 text-sm text-cream/70 transition hover:bg-white/5 hover:text-cream">
                    Send another
                  </button>
                  <button type="button" onClick={closeModal}
                          className="rounded-lg bg-purple px-4 py-2 text-sm font-medium text-white transition hover:bg-purple/90">
                    Done
                  </button>
                </div>
              </div>
            ) : (
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div role="radiogroup" aria-label="Kind of feedback" className="grid grid-cols-2 gap-1 rounded-lg bg-dark/80 p-1">
                {[['bug', Bug], ['feature', Lightbulb]].map(([key, Icon]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={type === key}
                    onClick={() => { setType(key); setSubmitError(''); }}
                    className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm transition ${
                      type === key ? 'bg-purple/30 text-cream shadow' : 'text-cream/60 hover:text-cream'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {FEEDBACK_KINDS[key].tab}
                  </button>
                ))}
              </div>

              <div>
                <label htmlFor="feedback-message" className="mb-1 block text-sm text-cream/80">
                  {copy.label}
                </label>
                <textarea
                  id="feedback-message"
                  ref={messageRef}
                  rows={4}
                  value={message}
                  onChange={(event) => { setMessage(event.target.value); if (submitError) setSubmitError(''); }}
                  placeholder={copy.placeholder}
                  aria-invalid={!!submitError && !message.trim()}
                  className={`${inputClass} resize-y`}
                />
              </div>

              <div>
                <label htmlFor="feedback-email" className="mb-1 block text-sm text-cream/80">
                  Email <span className="text-cream/40">(optional, if you would like a reply)</span>
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="feedback-media"
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-purple/40 bg-dark/70 px-3 py-2 text-sm text-cream/80 transition hover:border-purple/70 focus-within:ring-2 focus-within:ring-purple/40"
                >
                  <Paperclip className="h-4 w-4 shrink-0" />
                  <span className="truncate">{media ? media.name : 'Add a screenshot or video (optional)'}</span>
                </label>
                <input
                  id="feedback-media"
                  type="file"
                  accept={FEEDBACK_MEDIA_ACCEPT}
                  onChange={onAttachmentChange}
                  className="sr-only"
                />
                <p className="mt-1 text-xs text-cream/40">Images up to 10MB, videos up to 50MB.</p>
                {mediaError && <p className="mt-1 text-xs text-red-400" role="alert">{mediaError}</p>}
              </div>

              <input
                type="text"
                autoComplete="off"
                tabIndex={-1}
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              {submitError && <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{submitError}</div>}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg px-4 py-2 text-sm text-cream/70 transition hover:bg-white/5 hover:text-cream"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple px-4 py-2 text-sm font-medium text-white transition hover:bg-purple/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting
                    ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                    : <Send className="h-4 w-4" />}
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
